import React, { useState } from 'react';
import { ArrowLeft, Download, X, ZoomIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';

const Certificates = () => {
  const navigate = useNavigate();
  const [selectedCert, setSelectedCert] = useState(null);

  const certificates = [
    {
      id: 1,
      name: "APEDA Registration",
      fullName: "Agricultural and Processed Food Products Export Development Authority",
      description: "RCMC/APEDA Registration Certificate - Valid for export of all APEDA products",
      image: "https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/4woqvx6k_Apeda.jpeg",
      issueDate: "19/02/2026",
      validUpto: "18/02/2031",
      registrationNumber: "RCMC/APEDA/26804/2025-2026"
    },
    {
      id: 2,
      name: "FSSAI License",
      fullName: "Food Safety and Standards Authority of India",
      description: "Central License under FSS Act, 2006 for Trade/Retail - Trader/Merchant - Exporter",
      image: "https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/zxdnibhz_FSSAI.jpeg",
      issueDate: "06/02/2026",
      validUpto: "05/02/2027",
      licenseNumber: "11526998000095"
    },
    {
      id: 3,
      name: "IEC Certificate",
      fullName: "Importer-Exporter Code",
      description: "Ministry of Commerce and Industry - Directorate General of Foreign Trade",
      image: "https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/xuasggmu_IEC.jpeg",
      issueDate: "06/02/2026",
      validUpto: "Lifetime",
      iecNumber: "BDKPJ9349F"
    },
    {
      id: 4,
      name: "Spices Board Registration",
      fullName: "Certificate of Registration as Exporter of Spices",
      description: "Registered for export of spices and spice products under Spices Board Act 1986",
      image: "https://customer-assets.emergentagent.com/job_merchant-cornerstone/artifacts/n5ydkx2o_SPICE.jpeg",
      issueDate: "18/02/2026",
      validUpto: "17/02/2029",
      registrationNumber: "CRES/SBCB/27560/2025-2026"
    },
    {
      id: 5,
      name: "Coffee Board RCMC",
      fullName: "Coffee Board of India - Registration-Cum-Membership Certificate",
      description: "Registered exporter of coffee from India under the Coffee Board of India",
      image: "/certificates/coffee-rcmc.jpg",
      pdf: "/certificates/Coffee-RCMC.pdf",
      issueDate: "06/02/2026",
      validUpto: "31/03/2031",
      registrationNumber: "Exporter Code No. 3563"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-amber-50/30">
      {/* Navigation Header */}
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-800 text-white py-16 pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <div>
              <h1 className="text-4xl font-bold">Our Certifications</h1>
              <p className="text-green-100 mt-2 text-lg">Official export licenses and registrations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Certificates Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Cornerstone Exim holds all necessary certifications and licenses required for international agricultural exports. 
            All certificates are valid and regularly renewed to ensure compliance with export regulations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {certificates.map((cert) => (
            <div
              key={cert.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 group"
            >
              {/* Certificate Image */}
              <div 
                className="relative h-96 bg-gray-100 overflow-hidden cursor-pointer"
                onClick={() => setSelectedCert(cert)}
              >
                <img
                  src={cert.image}
                  alt={cert.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 shadow-xl">
                      <ZoomIn size={20} />
                      <span>View Full Size</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Certificate Info */}
              <div className="p-6">
                <div className="mb-4">
                  <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                    Valid Certificate
                  </span>
                  <h3 className="text-2xl font-bold text-amber-950 mb-2">{cert.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{cert.fullName}</p>
                  <p className="text-gray-700 leading-relaxed">{cert.description}</p>
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Issue Date:</span>
                    <span className="font-semibold text-amber-950">{cert.issueDate}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Valid Until:</span>
                    <span className="font-semibold text-green-700">{cert.validUpto}</span>
                  </div>
                  {cert.registrationNumber && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Registration:</span>
                      <span className="font-mono text-xs text-gray-800">{cert.registrationNumber}</span>
                    </div>
                  )}
                  {cert.licenseNumber && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">License:</span>
                      <span className="font-mono text-xs text-gray-800">{cert.licenseNumber}</span>
                    </div>
                  )}
                  {cert.iecNumber && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">IEC:</span>
                      <span className="font-mono text-xs text-gray-800">{cert.iecNumber}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setSelectedCert(cert)}
                  className="mt-4 w-full bg-green-700 hover:bg-green-800 text-white px-4 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <span>View Certificate</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Banner */}
        <div className="mt-16 bg-gradient-to-r from-green-700 to-green-800 rounded-2xl p-10 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Certified for International Trade</h2>
          <p className="text-green-50 text-lg max-w-3xl mx-auto">
            All our certifications are verified and registered with respective government authorities. 
            We maintain full compliance with international export regulations and food safety standards.
          </p>
        </div>
      </div>

      {/* Full Size Modal */}
      {selectedCert && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedCert(null)}>
          <button
            onClick={() => setSelectedCert(null)}
            className="absolute top-4 right-4 bg-white text-gray-900 p-3 rounded-full hover:bg-gray-100 transition-colors z-10"
          >
            <X size={24} />
          </button>
          <div className="max-w-5xl w-full bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 bg-gray-100 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">{selectedCert.name}</h3>
              <p className="text-sm text-gray-600">{selectedCert.fullName}</p>
            </div>
            <div className="max-h-[80vh] overflow-auto bg-white">
              <img
                src={selectedCert.image}
                alt={selectedCert.name}
                className="w-full h-auto"
              />
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <a
                href={selectedCert.pdf || selectedCert.image}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-700 hover:bg-green-800 text-white px-6 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
              >
                <Download size={18} />
                <span>Download</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certificates;
